<template>
  <div class="px-4 pb-1" v-if="!pickerExpanded">
    {{ $t("signup.choose_company_country") }}:
    {{ getCountryLabel(selectedCountry) }}
    <button @click="togglePicker" class="btn btn-link p-0">
      ({{ $t("common.edit") }})
    </button>
  </div>
  <div v-if="pickerExpanded" class="card p-4 mb-3">
    <h3 class="mb-1 mb-sm-2">
      {{ $t("signup.choose_company_country") }}
    </h3>
    <SearchableSelectComponent
      :options="countries"
      :modelValue="selectedCountry"
      :placeholder="$t('signup.select_country_placeholder')"
      @update:modelValue="handleCountrySelected"
    />
  </div>
</template>

<script setup lang="ts">
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { ALL_COUNTRIES, SUPPORTED_COUNTRIES_WITH_LABELS } from "@/constants";
import { useSignupStore } from "@/signup/signup";
import { differenceBy, intersectionBy, lowerCase, sortBy } from "lodash-es";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const { country: selectedCountry } = storeToRefs(useSignupStore());

const router = useRouter();

const supportedCountries = sortBy(
  intersectionBy(ALL_COUNTRIES, SUPPORTED_COUNTRIES_WITH_LABELS, "value"),
  ({ label }) => label
);

const unsupportedCountries = sortBy(
  differenceBy(ALL_COUNTRIES, SUPPORTED_COUNTRIES_WITH_LABELS, "value"),
  ({ label }) => label
);

// Format countries for SearchableSelectComponent (no disabled property needed)
const countries = [...supportedCountries, ...unsupportedCountries];

// Picker is expanded if locale is "en" (default) and collapsed if locale is "et" or "lv"
const { locale } = useI18n();
const pickerExpanded = ref(locale.value == "en" ? true : false);

const togglePicker = () => {
  pickerExpanded.value = !pickerExpanded.value;
};

/**
 * Get the display label for a country code
 */
const getCountryLabel = (countryCode: string): string => {
  if (!countryCode) return "";
  const country = countries.find((c) => c.value === countryCode);
  return country ? country.label : countryCode;
};

/**
 * Handle country selection from the SearchableSelectComponent
 */
const handleCountrySelected = async (country: string) => {
  if (!country) return;

  // A new country starts over: the step and the company or device ids belong to the previous one.
  // The signup store follows ?country, and ProducerSignupView remounts the flow for it.
  await router.push({
    path: "/signup/producer",
    query: { country: lowerCase(country) },
  });
};
</script>

<style scoped lang="scss"></style>
